import React, { useCallback, useRef, useState, DragEvent } from 'react'
import { observer } from 'mobx-react-lite'
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Connection,
  Edge,
  addEdge,
  useNodesState,
  useEdgesState,
  OnConnect,
  OnEdgesChange,
  OnNodesChange,
  ReactFlowInstance,
  Node,
  BackgroundVariant,
  NodeMouseHandler,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { nodeTypes } from './nodes'
import NodePalette from './NodePalette'
import WorkflowToolbar from './WorkflowToolbar'
import { NodeConfigPanel } from './NodeConfigPanel'
import { workflowStore } from '@/stores/WorkflowStore'
import type { WorkflowNode, WorkflowEdge } from '@/types'

let nodeId = 1
const getId = () => `node_${nodeId++}`

interface WorkflowCanvasProps {
  workflowId?: string
  readOnly?: boolean
}

const WorkflowCanvas = observer(({ readOnly = false }: WorkflowCanvasProps) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState(workflowStore.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(workflowStore.edges)
  const [reactFlowInstance, setReactFlowInstance] = React.useState<ReactFlowInstance | null>(
    null
  )
  const [selectedNode, setSelectedNode] = useState<Node | null>(null)

  // Sync with store
  React.useEffect(() => {
    setNodes(workflowStore.nodes)
  }, [workflowStore.nodes, setNodes])

  React.useEffect(() => {
    setEdges(workflowStore.edges)
  }, [workflowStore.edges, setEdges])

  React.useEffect(() => {
    workflowStore.setNodes(nodes as WorkflowNode[])
  }, [nodes])

  React.useEffect(() => {
    workflowStore.setEdges(edges as WorkflowEdge[])
  }, [edges])

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      const newEdge: Edge = {
        id: `edge_${connection.source}_${connection.target}`,
        source: connection.source!,
        target: connection.target!,
        type: 'smoothstep',
        animated: true,
      }
      setEdges((eds) => addEdge(newEdge, eds))
    },
    [setEdges]
  )

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()

      if (!reactFlowWrapper.current || !reactFlowInstance) return

      const type = event.dataTransfer.getData('application/reactflow')
      if (!type) return

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect()
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      })

      const newNode: Node = {
        id: getId(),
        type,
        position,
        data: {
          label: `${type} node`,
        },
      }

      setNodes((nds) => nds.concat(newNode))
    },
    [reactFlowInstance, setNodes]
  )

  const onDragStart = (event: DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType)
    event.dataTransfer.effectAllowed = 'move'
  }

  const handleNodesChange: OnNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes)
    },
    [onNodesChange]
  )

  const handleEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes)
    },
    [onEdgesChange]
  )

  const handleClear = useCallback(() => {
    if (window.confirm('Are you sure you want to clear the canvas?')) {
      setNodes([])
      setEdges([])
      workflowStore.clearCanvas()
    }
  }, [setNodes, setEdges])

  const handleAutoLayout = useCallback(() => {
    // Simple auto-layout algorithm
    const layoutNodes = nodes.map((node, index) => ({
      ...node,
      position: {
        x: 100 + (index % 3) * 300,
        y: 100 + Math.floor(index / 3) * 150,
      },
    }))
    setNodes(layoutNodes)
  }, [nodes, setNodes])

  // Handle node click to open configuration panel
  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    // Don't open config for start/end nodes
    if (node.type === 'start' || node.type === 'end') {
      return
    }
    setSelectedNode(node)
  }, [])

  // Handle saving node configuration
  const handleNodeConfigSave = useCallback((data: Record<string, any>) => {
    if (!selectedNode) return

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          return {
            ...node,
            data: {
              ...node.data,
              ...data,
            },
          }
        }
        return node
      })
    )
    setSelectedNode(null)
  }, [selectedNode, setNodes])

  // Handle closing the config panel
  const handleCloseConfigPanel = useCallback(() => {
    setSelectedNode(null)
  }, [])

  return (
    <div className="flex h-full">
      {!readOnly && <NodePalette onDragStart={onDragStart} />}

      <div className="flex-1 flex flex-col">
        {!readOnly && (
          <WorkflowToolbar
            onClear={handleClear}
            onAutoLayout={handleAutoLayout}
            canSave={workflowStore.canSave}
          />
        )}

        <div ref={reactFlowWrapper} className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            className="bg-gray-50"
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: true,
            }}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                const colors: Record<string, string> = {
                  start: '#22c55e',
                  end: '#ef4444',
                  llm: '#3b82f6',
                  agent: '#3b82f6',
                  tool: '#a855f7',
                  conditional: '#f97316',
                  loop: '#f97316',
                  humanInput: '#6b7280',
                }
                return colors[node.type || 'default'] || '#6b7280'
              }}
              maskColor="rgba(0, 0, 0, 0.1)"
            />
          </ReactFlow>
        </div>

        {nodes.length === 0 && !readOnly && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <svg
                className="w-24 h-24 mx-auto text-gray-300 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Start Building Your Workflow
              </h3>
              <p className="text-gray-500">
                Drag nodes from the palette to get started
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Node Configuration Panel */}
      {selectedNode && !readOnly && (
        <NodeConfigPanel
          nodeType={selectedNode.type || 'default'}
          nodeData={selectedNode.data}
          onSave={handleNodeConfigSave}
          onClose={handleCloseConfigPanel}
        />
      )}
    </div>
  )
})

export default WorkflowCanvas
